import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarDays, Filter } from 'lucide-react';

/**
 * FilterBar component for Resource Planning page
 * Handles time filters (Year, Month, KW) and entity filters (Employee, Project)
 */
const FilterBar = ({
  // Time filter state
  quickFilterYear,
  quickFilterMonth,
  quickFilterKW,
  setQuickFilterYear,
  setQuickFilterMonth,
  setQuickFilterKW,
  // Date range
  dateRange,
  setDateRange,
  // Filter actions
  applyYearFilter,
  applyKWFilter,
  getWeekDates,
  getKWRangeForMonth,
  // Entity filters
  filterEmployee,
  filterProject,
  setFilterEmployee,
  setFilterProject,
  // Data
  employees,
  projects
}) => {
  
  // Handle month change with auto-apply
  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setQuickFilterMonth(newMonth);
    setQuickFilterKW('');
    
    if (newMonth !== '' && quickFilterYear) {
      const month = parseInt(newMonth);
      const year = parseInt(quickFilterYear);
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      const monthStr = String(month + 1).padStart(2, '0');
      setDateRange({
        start: `${year}-${monthStr}-01`,
        end: `${year}-${monthStr}-${String(lastDayOfMonth).padStart(2, '0')}`
      });
    }
  };
  
  // Handle KW change with auto-apply
  const handleKWChange = (e) => {
    const newKW = e.target.value;
    setQuickFilterKW(newKW);
    
    if (newKW) {
      const kw = parseInt(newKW);
      const year = parseInt(quickFilterYear);
      const { start, end } = getWeekDates(kw, year);
      const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      setDateRange({
        start: formatDate(start),
        end: formatDate(end)
      });
    } else if (quickFilterMonth !== '' && quickFilterYear) {
      // Reset to full month
      const month = parseInt(quickFilterMonth);
      const year = parseInt(quickFilterYear);
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      const monthStr = String(month + 1).padStart(2, '0');
      setDateRange({
        start: `${year}-${monthStr}-01`,
        end: `${year}-${monthStr}-${String(lastDayOfMonth).padStart(2, '0')}`
      });
    }
  };
  
  // Generate KW options for the selected month
  const renderKWOptions = () => {
    const month = parseInt(quickFilterMonth);
    const year = parseInt(quickFilterYear);
    const { startKW, endKW } = getKWRangeForMonth(month, year);
    const options = [];
    
    if (startKW > endKW) {
      // Month spans year boundary
      for (let kw = startKW; kw <= 53; kw++) {
        options.push(<option key={kw} value={kw}>KW {kw}</option>);
      }
      for (let kw = 1; kw <= endKW; kw++) {
        options.push(<option key={`next-${kw}`} value={kw}>KW {kw}</option>);
      }
    } else {
      for (let kw = startKW; kw <= endKW; kw++) {
        options.push(<option key={kw} value={kw}>KW {kw}</option>);
      }
    }
    return options;
  };
  
  // Reset time filters to default
  const resetTimeFilters = () => {
    setQuickFilterYear(new Date().getFullYear().toString());
    setQuickFilterMonth('');
    setQuickFilterKW('');
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  return (
    <Card>
      <CardContent className="py-3">
        {/* Row 1: Time Filters */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">Zeitraum:</span>
          </div>
          
          {/* Year Filter */}
          <div className={`flex items-center gap-1 rounded-md p-1 ${quickFilterYear ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 border' : 'bg-muted/30 border border-transparent'}`}>
            <Label className="text-xs text-muted-foreground px-1">Jahr:</Label>
            <Input
              type="number"
              min="2020"
              max="2030"
              value={quickFilterYear}
              onChange={(e) => {
                setQuickFilterYear(e.target.value);
                if (e.target.value !== quickFilterYear) {
                  setQuickFilterMonth('');
                  setQuickFilterKW('');
                }
              }}
              className="w-20 h-7 text-xs bg-background"
            />
            <Button 
              variant={quickFilterYear ? "default" : "secondary"}
              size="sm" 
              className="h-7 px-2 text-xs"
              onClick={applyYearFilter}
            >
              Jahr
            </Button>
          </div>
          
          {/* Month Filter */}
          <div className={`flex items-center gap-1 rounded-md p-1 ${quickFilterMonth !== '' ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 border' : 'bg-muted/30 border border-transparent'}`}>
            <Label className="text-xs text-muted-foreground px-1">Monat:</Label>
            <select 
              value={quickFilterMonth}
              onChange={handleMonthChange}
              className="h-7 text-xs border border-slate-200 dark:border-slate-600 rounded-md px-2 bg-background min-w-[100px]"
              disabled={!quickFilterYear}
            >
              <option value="">--</option>
              <option value="0">Januar</option>
              <option value="1">Februar</option>
              <option value="2">März</option>
              <option value="3">April</option>
              <option value="4">Mai</option>
              <option value="5">Juni</option>
              <option value="6">Juli</option>
              <option value="7">August</option>
              <option value="8">September</option>
              <option value="9">Oktober</option>
              <option value="10">November</option>
              <option value="11">Dezember</option>
            </select>
          </div>
          
          {/* KW Filter */}
          <div className={`flex items-center gap-1 rounded-md p-1 ${quickFilterKW ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 border' : 'bg-muted/30 border border-transparent'}`}>
            <Label className="text-xs text-muted-foreground px-1">KW:</Label>
            {quickFilterMonth !== '' && quickFilterYear ? (
              <select
                value={quickFilterKW}
                onChange={handleKWChange}
                className="h-7 text-xs border border-slate-200 dark:border-slate-600 rounded-md px-2 bg-background min-w-[70px]"
              >
                <option value="">--</option>
                {renderKWOptions()}
              </select>
            ) : (
              <Input
                type="number"
                min="1"
                max="53"
                placeholder="1-53"
                value={quickFilterKW}
                onChange={(e) => setQuickFilterKW(e.target.value)}
                className="w-16 h-7 text-xs"
                onKeyDown={(e) => e.key === 'Enter' && applyKWFilter()}
                disabled={!quickFilterYear}
              />
            )}
            {!(quickFilterMonth !== '' && quickFilterYear) && (
              <Button 
                variant={quickFilterKW ? "default" : "secondary"}
                size="sm" 
                className="h-7 px-2 text-xs"
                onClick={applyKWFilter}
                disabled={!quickFilterYear}
              >
                KW
              </Button>
            )}
          </div>
          
          <div className="border-l border-slate-300 dark:border-slate-700 h-8" />
          
          {/* Manual Date Range */}
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Von:</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-36 h-7 text-xs"
            />
            <Label className="text-xs text-muted-foreground">Bis:</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-36 h-7 text-xs"
            />
          </div>
          
          {/* Reset Time Filters */}
          {(quickFilterYear || quickFilterMonth !== '' || quickFilterKW) && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs text-muted-foreground"
              onClick={resetTimeFilters}
            >
              Zurücksetzen
            </Button>
          )}
        </div>
        
        {/* Row 2: Entity Filters */}
        <div className="flex items-center gap-6 pt-3 mt-3 border-t border-slate-300 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">Filter:</span>
          </div>
          
          {/* Employee Filter */}
          <div className="flex items-center gap-1 rounded-md p-1 bg-muted/30">
            <Label className="text-xs text-muted-foreground px-1">Mitarbeiter:</Label>
            <select 
              value={filterEmployee}
              onChange={(e) => setFilterEmployee(e.target.value)}
              className="h-7 text-xs border border-slate-200 dark:border-slate-600 rounded-md px-2 bg-background min-w-[140px]"
            >
              <option value="">Alle</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          
          {/* Project Filter */}
          <div className="flex items-center gap-1 rounded-md p-1 bg-muted/30">
            <Label className="text-xs text-muted-foreground px-1">Projekt:</Label>
            <select 
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="h-7 text-xs border border-slate-200 dark:border-slate-600 rounded-md px-2 bg-background min-w-[140px]"
            >
              <option value="">Alle</option>
              {projects.map(proj => (
                <option key={proj.id} value={proj.id}>{proj.name}</option>
              ))}
            </select>
          </div>
          
          {/* Reset Entity Filters */}
          {(filterEmployee || filterProject) && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs text-muted-foreground"
              onClick={() => {
                setFilterEmployee('');
                setFilterProject('');
              }}
            >
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
